/**
 * Hides a turn's follow-up chip that says what its assist card says. Kept apart from the card's parser
 * (`assist-suggestion.ts`) so the chat's first page, which draws the chips, does not carry it: the card
 * itself is loaded on demand.
 */
import type { AssistSuggestion } from '@server/assist';
import { titleSimilarity } from '@server/title-similarity';

/**
 * A follow-up chip at or above this similarity to a card's title (or its note's title) says what the card
 * says, and is hidden. Above the server's note near-duplicate bar (0.6): a chip that only names the same
 * term ("Show active accounts" beside a note titled "Active account", 0.67) asks something else and stays.
 */
export const CHIP_DUPLICATES_CARD_THRESHOLD = 0.75;

/**
 * The titles a chip is compared with, read from a card's stored content: the card's own and, on a note
 * card, the note's. Not the full validation `parseAssistSuggestion` does: a card that fails it renders
 * nothing, and its chip is compared with what the content says all the same.
 */
export function assistCardTitles(content: string): string[] {
  let value: unknown;
  try {
    value = JSON.parse(content);
  } catch {
    return [];
  }
  if (typeof value !== 'object' || value === null) return [];
  const card = value as Partial<Record<keyof AssistSuggestion | 'noteTitle', unknown>>;
  return [card.title, card.kind === 'propose_note' ? card.noteTitle : undefined].filter(
    (title): title is string => typeof title === 'string' && title.trim().length > 0,
  );
}

/** True when a follow-up chip says nearly what one of the turn's cards says (`titles`, from `assistCardTitles`). */
export function chipDuplicatesCard(label: string, titles: readonly string[]): boolean {
  return titles.some((title) => titleSimilarity(label, title) >= CHIP_DUPLICATES_CARD_THRESHOLD);
}
